"use client";

import { useEffect, useRef, type Dispatch, type SetStateAction } from "react";
import { useUserContext } from "@/app/_module/store/contexts/userContext/userContexts";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { DEFAULT_FILTERS, type LeadFilters } from "../components/LeadsSearch";
import { EMPTY_LEAD_VIEWS, type LeadView, type LeadViewsPref } from "../utils/leadViews";

interface Options {
  filters: LeadFilters;
  setFilters: Dispatch<SetStateAction<LeadFilters>>;
  sortBy: string;
  setSortBy: (s: string) => void;
  limit: number;
  setLimit: (n: number) => void;
  /** Max page size this page offers (desktop 500, mobile 100). */
  maxLimit: number;
  /** The tab already had working filters when the page opened → a default view must not override them. */
  hadSessionFilters: boolean;
  /** Deep link (listName / tag / query) → same: leave what the link asked for. */
  isDeepLink: boolean;
  /** Extra reset when a view is applied (selection, page). */
  onApplied?: () => void;
}

/**
 * Per-user Leads table settings, shared by /leads and /m/leads:
 *   server → state: appPreferences.tableColumns.leads mirrors into filters.visibleColumns
 *   state → server: ONLY for column edits flagged via `noteFiltersChange` (an
 *     effect that wrote "whenever it differs" overwrote saved columns on sign-in)
 *   saved views (filters + columns + sort + page size) with a default that opens
 *     on a fresh visit.
 */
export function useLeadViews(o: Options) {
  const { user, refetchUser } = useUserContext();
  const { filters, setFilters, sortBy, setSortBy, limit, setLimit } = o;

  const savedColumns = (user as any)?.user?.appPreferences?.tableColumns?.leads as string[] | undefined;
  const savedColumnsJson = Array.isArray(savedColumns) ? JSON.stringify(savedColumns) : null;
  const appliedColumnsRef = useRef<string | null>(null);
  useEffect(() => {
    if (!savedColumnsJson || appliedColumnsRef.current === savedColumnsJson) return;
    appliedColumnsRef.current = savedColumnsJson;
    setFilters((prev) => (JSON.stringify(prev.visibleColumns) === savedColumnsJson ? prev : { ...prev, visibleColumns: savedColumns! }));
  }, [savedColumnsJson, savedColumns, setFilters]);

  const persistColumns = (cols: string[]) => {
    if (!(user as any)?.user || JSON.stringify(cols) === savedColumnsJson) return;
    const tableColumns = { ...((user as any)?.user?.appPreferences?.tableColumns || {}), leads: cols };
    appliedColumnsRef.current = JSON.stringify(cols);
    refetchUser((prev: any) => ({
      ...prev,
      user: { ...prev.user, appPreferences: { ...(prev.user?.appPreferences || {}), tableColumns } },
    }));
    api.patch({ url: getApiUrl("/users/profile/app-preferences"), body: { tableColumns }, auth: true }).catch(() => {});
  };
  const columnsEditedRef = useRef(false);
  useEffect(() => {
    if (!columnsEditedRef.current) return;
    columnsEditedRef.current = false;
    persistColumns(filters.visibleColumns);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.visibleColumns]);
  /** Call from onFiltersChange BEFORE setFilters so a column edit gets written. */
  const noteFiltersChange = (next: LeadFilters) => {
    if (JSON.stringify(next.visibleColumns) !== JSON.stringify(filters.visibleColumns)) columnsEditedRef.current = true;
  };

  const leadViews: LeadViewsPref = ((user as any)?.user?.appPreferences?.views?.leads as LeadViewsPref | undefined) ?? EMPTY_LEAD_VIEWS;
  const persistLeadViews = (next: LeadViewsPref) => {
    const views = { ...((user as any)?.user?.appPreferences?.views || {}), leads: next };
    refetchUser((prev: any) => ({
      ...prev,
      user: { ...prev.user, appPreferences: { ...(prev.user?.appPreferences || {}), views } },
    }));
    // PATCH shallow-merges top-level keys: send the whole `views` object.
    api.patch({ url: getApiUrl("/users/profile/app-preferences"), body: { views }, auth: true }).catch(() => {});
  };
  const applyLeadView = (view: LeadView) => {
    const next = { ...DEFAULT_FILTERS, ...view.filters };
    persistColumns(next.visibleColumns); // the view's columns become the saved columns
    setFilters(next);
    setSortBy(view.sortBy);
    setLimit(Math.min(view.limit || 50, o.maxLimit));
    o.onApplied?.();
  };
  const leadViewActions = {
    views: leadViews,
    onApply: applyLeadView,
    onSave: (name: string) => {
      const view: LeadView = { id: Date.now().toString(36), name, filters, sortBy, limit, createdAt: new Date().toISOString() };
      persistLeadViews({ ...leadViews, items: [...leadViews.items, view] });
    },
    onDelete: (id: string) =>
      persistLeadViews({ items: leadViews.items.filter((v) => v.id !== id), defaultId: leadViews.defaultId === id ? null : leadViews.defaultId }),
    onSetDefault: (id: string | null) => persistLeadViews({ ...leadViews, defaultId: id }),
  };
  // Fresh open + a default view + no deep link → open the page the way they manage it.
  const defaultViewAppliedRef = useRef(false);
  useEffect(() => {
    if (defaultViewAppliedRef.current || !leadViews.defaultId) return;
    if (o.hadSessionFilters || o.isDeepLink) {
      defaultViewAppliedRef.current = true;
      return;
    }
    const view = leadViews.items.find((v) => v.id === leadViews.defaultId);
    if (!view) return;
    defaultViewAppliedRef.current = true;
    applyLeadView(view);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leadViews.defaultId, leadViews.items.length]);

  return { leadViewActions, noteFiltersChange };
}
